// frontend/src/pages/ListaReparaciones.jsx
// LISTA DE ORDENES DE TRABAJO CORRECTIVAS (OTC) - estilo mantenimiento
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import Table from '../components/common/Table.jsx';
import Modal from '../components/common/Modal.jsx';
import ConfiguracionBase from '../components/configuracion/ConfiguracionBase.jsx';
import { LinkEquipo } from '../components/common/Links.jsx';
import { ActionButton, OutlinedButton } from '../components/common/Buttons.jsx';
import SearchableSelectField from '../components/common/SearchableSelectField.jsx';
import DynamicFilterBuilder from '../components/common/DynamicFilterBuilder.jsx';
import equiposService from '../services/equiposService.js';
import otcService from '../services/otcService.js';
import detencionesService from '../services/detencionesService.js';
import standByService from '../services/standByService.js';
import MultiSelectDropdown from '../components/common/MultiSelectDropdown.jsx';
import { apiClient } from '../services/apiClient';
import uiPreferencesService from '../services/uiPreferencesService.js';
import { formatDateDisplay, hoyArgentina } from '../utils/dateUtils';
import {
  DashboardStatCard,
  PageTypographyScope,
  UnifiedStatusBadge,
} from '../components/common/OperationalVisuals.jsx';
import { useToast } from '../components/common/Toast';
import { useConfirm } from '../components/common/ModalConfirmacion';
import { useEntrada } from '../components/common/ModalEntrada';
import { useAuth } from '../hooks/useAuth';
import useListaHidratada from '../hooks/useListaHidratada';
import { ModuloHeader, SeccionBar, SeccionTitulo, SeccionAcciones,
  SeccionBuscador, ESTILO_BOTON_SECCION } from '../components/common/ModuloHeader.jsx';

const REPARACIONES_CLOUD_SCOPE = 'reparaciones.filters.v2';

const TABS = [
  { key: 'activas', label: 'OT Correctivas' },
  { key: 'historial', label: 'Historial' },
  { key: 'fuera_servicio', label: 'Equipos F/S' },
  { key: 'stand_by', label: 'Equipos Stand by' },
];

const HELP = {
  prereqs: [
    'Tener cargados los mecánicos, los estados de reparación y los motivos de detención.',
  ],
  tips: [
    'Una orden correctiva registra una falla concreta; marcar el equipo fuera de servicio registra que dejó de operar.',
    'Las tarjetas de arriba filtran la lista: tocá "Atrasadas" para ver las que pasaron su fecha planificada.',
    'Cargar el motivo de detención es lo que después permite analizar por qué se para la flota.',
    'Los repuestos que agregues a la orden descuentan stock del almacén al usarse.',
  ],
  nextSteps: [
    'Asignar un mecánico responsable a la orden de trabajo, permite que pueda acceder al la misma desde su dispositivo.',
    'Cerrar la orden cuando el trabajo esté terminado, con sus horas y repuestos cargados.',
    'Revisar el informe de trabajos pendientes para detectar reparaciones demoradas.',
    'Registrar las detenciones para poder evaluar correctamente la disponibilidad de la flota.',
  ],
};

const PageShell = styled(PageTypographyScope)`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const HydrationNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 6px;
  min-height: 0;
  overflow: hidden;
`;

const ClickableStatCard = styled(DashboardStatCard)`
  cursor: pointer;
  border-color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.borderDefault)};
  box-shadow: ${({ $active, theme }) => ($active ? `0 0 0 1px ${theme.colors.primary}55` : 'none')};
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 2px 0;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 6px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.surface};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.txtSecondary};
`;

const ChipButtonX = styled.button`
  border: none;
  background: ${({ theme }) => theme.colors.transparent};
  color: inherit;
  cursor: pointer;
  padding: 0;
  line-height: 1;
`;

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const RowActions = styled.div`
  display: inline-flex;
  gap: 6px;
  align-items: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease;

  tr:hover &, tr:focus-within & {
    opacity: 1;
    pointer-events: auto;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;

  label {
    font-size: 11px;
    color: ${({ theme }) => theme.colors.txtSecondary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  }

  input, select, textarea {
    height: 30px;
    border: 1px solid ${({ theme }) => theme.colors.borderDefault};
    border-radius: 6px;
    padding: 0 8px;
    background: ${({ theme }) => theme.colors.bgSecundario};
    color: ${({ theme }) => theme.colors.txtPrincipal};
    font-size: 12px;
    width: 100%;
  }

  textarea {
    min-height: 88px;
    resize: vertical;
    padding: 8px;
  }
`;

const AutoInfo = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface};
  padding: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.txtSecondary};
`;

const OtcInlineLink = styled.button`
  border: none;
  background: ${({ theme }) => theme.colors.transparent};
  color: ${({ theme }) => theme.colors.primary};
  padding: 0;
  margin: 0;
  font-size: 12px;
  line-height: 1.2;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;

const FieldWithAdd = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;

  > :first-child {
    flex: 1;
    min-width: 0;
  }
`;

const AddFkButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border-radius: 6px;
  width: 30px;
  height: 30px;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const OtcLinksStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const normalizeDateInput = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const match = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!match) return '';
  const [, dd, mm, yyyy] = match;
  return `${yyyy}-${mm}-${dd}`;
};

const isDateAfterToday = (dateYmd, todayYmd) => {
  if (!dateYmd) return false;
  return String(dateYmd) > String(todayYmd);
};

const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === '') return [];
  return [String(value)];
};

const ListaReparaciones = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const today = hoyArgentina();
  const { toast } = useToast();
  const { confirmar } = useConfirm();
  const { entrada } = useEntrada();
  const { hasPermission } = useAuth();
  const canDeleteOtc = hasPermission('ordentrabajocorrectiva', 'delete');

  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [equipoMap, setEquipoMap] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const [tab, setTab] = useState(
    ['activas', 'historial', 'fuera_servicio', 'stand_by'].includes(initialTab) ? initialTab : 'activas'
  );
  const [helpOpen, setHelpOpen] = useState(false);

  // Carga hidratada: primera página al instante, resto en segundo plano.
  // Los filtros en memoria terminan cubriendo el 100 % de los registros
  // (antes había un corte silencioso en 1000 filas).
  const {
    rows,
    hidratando: hidratandoOtc,
    completo: completoOtc,
    cargandoInicial: loading,
    progreso: progresoOtc,
    refetch: refetchOtc,
  } = useListaHidratada({
    endpoint: 'ordenes-trabajo-correctivas/',
    params: { ordering: '-fecha_apertura' },
  });
  const {
    rows: detencionesRows,
    hidratando: hidratandoDet,
    completo: completoDet,
    refetch: refetchDetenciones,
  } = useListaHidratada({
    endpoint: 'detenciones-equipos/',
    params: { ordering: '-fecha_ingreso' },
  });
  // Stand by: equipos parados sin estar fuera de servicio. Su próximo
  // mantenimiento se proyecta desde hoy mientras el período siga abierto.
  const {
    rows: standByRows,
    hidratando: hidratandoSb,
    completo: completoSb,
    refetch: refetchStandBy,
  } = useListaHidratada({
    endpoint: 'stand-by-equipos/',
    params: { ordering: '-fecha_inicio' },
  });
  const [standBySortColumn, setStandBySortColumn] = useState('fecha_inicio');
  const [standBySortDirection, setStandBySortDirection] = useState('desc');
  const [showStandByModal, setShowStandByModal] = useState(false);
  const [editingStandBy, setEditingStandBy] = useState(null);
  const [savingStandBy, setSavingStandBy] = useState(false);
  const [standByForm, setStandByForm] = useState({
    equipos: [],
    fecha_inicio: today,
    fecha_fin: '',
    motivo: '',
    observaciones: '',
  });
  const [sortColumn, setSortColumn] = useState('fecha_apertura');
  const [sortDirection, setSortDirection] = useState('desc');
  const [detencionesSortColumn, setDetencionesSortColumn] = useState('fecha_ingreso');
  const [detencionesSortDirection, setDetencionesSortDirection] = useState('desc');
  const [showDetencionModal, setShowDetencionModal] = useState(false);
  const [editingDetencion, setEditingDetencion] = useState(null);
  const [savingDetencion, setSavingDetencion] = useState(false);
  const [motivosOpc, setMotivosOpc] = useState([]);
  const [estadosEquipoOpc, setEstadosEquipoOpc] = useState([]);
  const [otcAbiertasEquipo, setOtcAbiertasEquipo] = useState([]);
  const [quickCreateConfig, setQuickCreateConfig] = useState(null);
  const [detencionForm, setDetencionForm] = useState({
    equipo: '',
    motivo_detencion: '',
    estado_equipo: '',
    motivo: 'otras',
    fecha_ingreso: today,
    fecha_egreso_prevista: '',
    fecha_egreso_real: '',
    observaciones: '',
    otcs_asociadas_ids: [],
  });
  const [filters, setFilters] = useState({
    q: '',
    fecha_desde: '',
    fecha_hasta: '',
  });
  const [dynamicFilters, setDynamicFilters] = useState({
    columnFilters: {},
    touchedFilters: {},
    expandedFilterGroups: {},
  });
  const filtrosHydratedRef = useRef(false);

  const refreshDetencionCatalogos = useCallback(async () => {
    try {
      const [motivosResp, estadosResp] = await Promise.all([
        apiClient.get('/motivosdetencion/', { params: { page_size: 1000, ordering: 'nombre' } }),
        apiClient.get('/estados/', { params: { page_size: 1000, ordering: 'nombre' } }),
      ]);
      setMotivosOpc(motivosResp?.data?.results || motivosResp?.data || []);
      setEstadosEquipoOpc(estadosResp?.data?.results || estadosResp?.data || []);
    } catch (error) {
      console.error('Error refrescando catalogos de detenciones:', error);
    }
  }, []);

  const openQuickCreateModal = useCallback((type) => {
    if (type === 'motivo') {
      setQuickCreateConfig({
        type,
        apiEndpoint: 'motivosdetencion',
        titulo: 'Motivos de Detención',
        descripcion: 'Gestioná los motivos de detención de equipos',
        icon: 'bi bi-wrench-adjustable-circle',
      });
    } else if (type === 'estado') {
      setQuickCreateConfig({
        type,
        apiEndpoint: 'estados',
        titulo: 'Estados de Equipo',
        descripcion: 'Gestioná los estados de equipo',
        icon: 'bi bi-flag',
      });
    }
  }, []);

  const closeQuickCreateModal = useCallback(() => {
    setQuickCreateConfig(null);
  }, []);

  const getEstadoOperativo = useCallback((row) => {
    if (!row) return '-';
    if (row.estado === 'abierta') {
      if (row.atrasada) return 'atrasada';
      if (row.fecha_cierre_planificada) return 'programada';
      return 'sin_planificacion';
    }
    return row.estado;
  }, []);

  const clearEstadoFilter = useCallback(() => {
    setDynamicFilters((prev) => {
      const nextFilters = { ...(prev.columnFilters || {}) };
      const nextTouched = { ...(prev.touchedFilters || {}) };
      delete nextFilters.estado;
      delete nextTouched.estado;
      return { ...prev, columnFilters: nextFilters, touchedFilters: nextTouched };
    });
  }, []);

  const applyCardFilter = useCallback((target) => {
    const setEstado = (value) => {
      setDynamicFilters((prev) => ({
        ...prev,
        columnFilters: {
          ...(prev.columnFilters || {}),
          estado: [value],
        },
        touchedFilters: {
          ...(prev.touchedFilters || {}),
          estado: true,
        },
      }));
    };

    if (target === 'abierta') {
      setTab('activas');
      clearEstadoFilter();
      return;
    }
    if (target === 'programada' || target === 'atrasada' || target === 'sin_planificacion') {
      setTab('activas');
      setEstado(target === 'sin_planificacion' ? 'sin planificacion' : target);
      return;
    }
    if (target === 'cerrada' || target === 'cancelada') {
      setTab('historial');
      setEstado(target);
      return;
    }
    if (target === 'fuera_servicio') {
      setTab('fuera_servicio');
      setEstado('Fuera de Servicio');
      return;
    }
    if (target === 'todas_detenciones') {
      setTab('fuera_servicio');
      clearEstadoFilter();
      return;
    }
    if (target === 'stand_by') {
      setTab('stand_by');
      setEstado('En stand by');
    }
  }, [clearEstadoFilter]);

  const isCardActive = useCallback((target) => {
    const selectedEstado = ensureArray(dynamicFilters.columnFilters?.estado);
    const touchedEstado = !!dynamicFilters.touchedFilters?.estado;

    if (target === 'abierta') return tab === 'activas' && !touchedEstado;
    if (target === 'programada' || target === 'atrasada' || target === 'sin_planificacion') {
      const expected = target === 'sin_planificacion' ? 'sin planificacion' : target;
      return tab === 'activas' && touchedEstado && selectedEstado.length === 1 && selectedEstado[0] === expected;
    }
    if (target === 'cerrada' || target === 'cancelada') {
      return tab === 'historial' && touchedEstado && selectedEstado.length === 1 && selectedEstado[0] === target;
    }
    if (target === 'fuera_servicio') {
      return tab === 'fuera_servicio' && touchedEstado && selectedEstado.length === 1 && selectedEstado[0] === 'Fuera de Servicio';
    }
    if (target === 'todas_detenciones') {
      return tab === 'fuera_servicio' && !touchedEstado;
    }
    if (target === 'stand_by') {
      return tab === 'stand_by' && touchedEstado && selectedEstado.length === 1 && selectedEstado[0] === 'En stand by';
    }
    return false;
  }, [tab, dynamicFilters]);

  // Persiste la pestaña activa en la URL para que "Volver" desde una OTC
  // (navigate(-1)) restaure la misma pestaña en vez de caer siempre en "activas".
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  }, [tab, searchParams, setSearchParams]);

  // Compatibilidad con los llamadores existentes (post-guardado/cierre):
  // disparan la recarga hidratada completa.
  const loadData = useCallback(async () => {
    refetchOtc();
  }, [refetchOtc]);

  const loadDetenciones = useCallback(async () => {
    refetchDetenciones();
  }, [refetchDetenciones]);

  useEffect(() => {
    let active = true;
    const loadFiltersData = async () => {
      try {
        const equiposData = await equiposService.getTodosEquipos();
        if (!active) return;
        const equipos = equiposData?.data || [];
        setEquipoMap(Object.fromEntries(equipos.map((e) => [e.id, e])));
        await refreshDetencionCatalogos();
      } catch (e) {
        console.error('Error cargando filtros OTC', e);
      }
    };
    loadFiltersData();
    return () => { active = false; };
  }, [refreshDetencionCatalogos]);


  const applyStoredPreferences = useCallback((parsed) => {
    if (!parsed || typeof parsed !== 'object') return;

    if (parsed.filters && typeof parsed.filters === 'object') {
      setFilters((prev) => ({
        ...prev,
        q: typeof parsed.filters.q === 'string' ? parsed.filters.q : prev.q,
        fecha_desde: typeof parsed.filters.fecha_desde === 'string' ? parsed.filters.fecha_desde : prev.fecha_desde,
        fecha_hasta: typeof parsed.filters.fecha_hasta === 'string' ? parsed.filters.fecha_hasta : prev.fecha_hasta,
      }));
    }

    if (parsed.dynamicFilters && typeof parsed.dynamicFilters === 'object') {
      setDynamicFilters({
        columnFilters: parsed.dynamicFilters.columnFilters || {},
        touchedFilters: parsed.dynamicFilters.touchedFilters || {},
        expandedFilterGroups: parsed.dynamicFilters.expandedFilterGroups || {},
      });
    }
  }, []);

  useEffect(() => {
    let active = true;

    const hydratePreferences = async () => {
      try {
        const cloudValue = await uiPreferencesService.getScope(REPARACIONES_CLOUD_SCOPE);
        if (!active || !cloudValue) return;
        applyStoredPreferences(cloudValue);
      } catch (error) {
        console.warn('No se pudieron restaurar filtros cloud de reparaciones', error);
      } finally {
        if (active) filtrosHydratedRef.current = true;
      }
    };

    filtrosHydratedRef.current = false;
    hydratePreferences();

    return () => {
      active = false;
    };
  }, [applyStoredPreferences]);

  useEffect(() => {
    if (!filtrosHydratedRef.current) return;

    const payload = {
      filters,
      dynamicFilters,
    };

    const timeoutId = setTimeout(() => {
      uiPreferencesService.setScope(REPARACIONES_CLOUD_SCOPE, payload).catch((error) => {
        console.warn('No se pudieron guardar filtros cloud de reparaciones', error);
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [dynamicFilters, filters]);

  const getInfraFilterValue = useCallback((row, key) => {
    const eq = equipoMap[row?.equipo] || {};
    // Las filas de stand by traen esta_activo (las de detención, esta_activa).
    const esStandBy = !!row && Object.prototype.hasOwnProperty.call(row, 'esta_activo');
    if (esStandBy && key === 'estado') return row.esta_activo ? 'En stand by' : 'Finalizado';
    if (esStandBy && key === 'motivo') return String(row.motivo || '').trim() || '-';

    if (key === 'sucursal') return String(eq.sucursal_nombre || '-').trim();
    if (key === 'familia') return String(eq.familia_nombre || '-').trim();
    if (key === 'tipo') return String(eq.tipo_nombre || '-').trim();
    if (key === 'marca') return String(eq.marca_nombre || '-').trim();
    if (key === 'modelo') return String(eq.modelo_nombre || '-').trim();
    if (key === 'equipo') {
      const interno = row?.equipo_interno || eq.interno || eq.codigo_interno || row?.equipo || '-';
      const descripcion = row?.equipo_descripcion || eq.descripcion || '';
      return descripcion ? `${interno} - ${descripcion}` : String(interno);
    }
    if (key === 'mecanico') return String(row?.mecanico_responsable_nombre || '-').trim();
    if (key === 'titulo') return String(row?.titulo || '-').trim();
    if (key === 'motivo') return String(row?.motivo_detencion_nombre || row?.motivo_display || '-').trim();
    if (key === 'estado_equipo') return String(row?.estado_equipo_nombre || '-').trim();
    if (key === 'estado_reparacion') return String(row?.estado_reparacion_nombre || '-').trim();
    if (key === 'estado') {
      if (row?.motivo_detencion_nombre || row?.estado_display) {
        return String(row?.estado_display || row?.estado || '-').trim();
      }
      const estadoOperativo = getEstadoOperativo(row);
      return estadoOperativo === 'sin_planificacion' ? 'sin planificacion' : String(estadoOperativo || '-').trim();
    }
    return '';
  }, [equipoMap, getEstadoOperativo]);

  const filterColumns = useMemo(() => {
    if (tab === 'stand_by') {
      return [
        { key: 'estado', label: 'Estado', getValue: (row) => getInfraFilterValue(row, 'estado') },
        { key: 'motivo', label: 'Motivo', getValue: (row) => getInfraFilterValue(row, 'motivo') },
        { key: 'equipo', label: 'Equipo', getValue: (row) => getInfraFilterValue(row, 'equipo') },
        { key: 'sucursal', label: 'Sucursal', getValue: (row) => getInfraFilterValue(row, 'sucursal') },
        { key: 'familia', label: 'Familia', getValue: (row) => getInfraFilterValue(row, 'familia') },
        { key: 'tipo', label: 'Tipo de equipo', getValue: (row) => getInfraFilterValue(row, 'tipo') },
        { key: 'marca', label: 'Marca', getValue: (row) => getInfraFilterValue(row, 'marca') },
        { key: 'modelo', label: 'Modelo', getValue: (row) => getInfraFilterValue(row, 'modelo') },
      ];
    }
    if (tab === 'fuera_servicio') {
      return [
        { key: 'estado', label: 'Estado de detencion', getValue: (row) => getInfraFilterValue(row, 'estado') },
        { key: 'estado_equipo', label: 'Estado de equipo', getValue: (row) => getInfraFilterValue(row, 'estado_equipo') },
        { key: 'motivo', label: 'Motivo', getValue: (row) => getInfraFilterValue(row, 'motivo') },
        { key: 'equipo', label: 'Equipo', getValue: (row) => getInfraFilterValue(row, 'equipo') },
        { key: 'sucursal', label: 'Sucursal', getValue: (row) => getInfraFilterValue(row, 'sucursal') },
        { key: 'familia', label: 'Familia', getValue: (row) => getInfraFilterValue(row, 'familia') },
        { key: 'tipo', label: 'Tipo de equipo', getValue: (row) => getInfraFilterValue(row, 'tipo') },
        { key: 'marca', label: 'Marca', getValue: (row) => getInfraFilterValue(row, 'marca') },
        { key: 'modelo', label: 'Modelo', getValue: (row) => getInfraFilterValue(row, 'modelo') },
      ];
    }

    return [
      { key: 'estado', label: 'Estado', getValue: (row) => getInfraFilterValue(row, 'estado') },
      { key: 'equipo', label: 'Equipo', getValue: (row) => getInfraFilterValue(row, 'equipo') },
      { key: 'sucursal', label: 'Sucursal', getValue: (row) => getInfraFilterValue(row, 'sucursal') },
      { key: 'familia', label: 'Familia', getValue: (row) => getInfraFilterValue(row, 'familia') },
      { key: 'tipo', label: 'Tipo de equipo', getValue: (row) => getInfraFilterValue(row, 'tipo') },
      { key: 'marca', label: 'Marca', getValue: (row) => getInfraFilterValue(row, 'marca') },
      { key: 'modelo', label: 'Modelo', getValue: (row) => getInfraFilterValue(row, 'modelo') },
      { key: 'mecanico', label: 'Mecánico responsable', getValue: (row) => getInfraFilterValue(row, 'mecanico') },
      { key: 'estado_equipo', label: 'Estado equipo', getValue: (row) => String(row?.estado_equipo_nombre || '-').trim() },
      { key: 'estado_reparacion', label: 'Situación', getValue: (row) => getInfraFilterValue(row, 'estado_reparacion') },
      { key: 'titulo', label: 'Titulo', getValue: (row) => getInfraFilterValue(row, 'titulo') },
    ];
  }, [getInfraFilterValue, tab]);

  const columns = [
    {
      key: 'numero_ot',
      label: 'Nro. OTC',
      sortable: true,
      sortKey: 'numero_ot',
      width: '11%'
    },
    {
      key: 'equipo_interno',
      label: 'Equipo',
      sortable: true,
      sortKey: 'equipo_interno',
      width: '11%',
      render: (value, item) => (
        <LinkEquipo
          href={`/?equipoId=${item.equipo}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.location.href = `/?equipoId=${item.equipo}`;
          }}
        >
          {value}
        </LinkEquipo>
      )
    },
    {
      key: 'titulo',
      label: 'Título',
      sortable: true,
      sortKey: 'titulo',
      width: '210px'
    },
    {
      key: 'estado',
      label: 'Estado',
      sortable: true,
      sortKey: 'estado',
      width: '12%',
      render: (v, row) => {
        if (v === 'abierta' && row.atrasada) return <UnifiedStatusBadge $tone="danger">ATRASADA</UnifiedStatusBadge>;
        if (v === 'abierta' && row.fecha_cierre_planificada) return <UnifiedStatusBadge $tone="info">PROGRAMADA</UnifiedStatusBadge>;
        if (v === 'abierta') return <UnifiedStatusBadge $tone="neutral">SIN PLANIFICACIÓN</UnifiedStatusBadge>;
        if (v === 'cerrada') return <UnifiedStatusBadge $tone="success">CERRADA</UnifiedStatusBadge>;
        if (v === 'cancelada') return <UnifiedStatusBadge $tone="warning">CANCELADA</UnifiedStatusBadge>;
        return <UnifiedStatusBadge $tone="info">-</UnifiedStatusBadge>;
      }
    },
    {
      key: 'estado_reparacion',
      label: 'Situación',
      sortable: false,
      width: '140px',
      render: (v, row) => row.estado_reparacion_nombre || <span style={{ opacity: 0.3 }}>—</span>
    },
    {
      key: 'mecanico_responsable_nombre',
      label: 'Mecánico',
      sortable: true,
      sortKey: 'mecanico_responsable_nombre',
      width: '130px',
      render: (v) => v ? v : <span style={{ opacity: 0.45 }}>Sin asignar</span>
    },
    {
      key: 'estado_equipo_nombre',
      label: 'Estado equipo',
      sortable: false,
      width: '120px',
      render: (v) => v || '—'
    },
    {
      key: 'fecha_apertura',
      label: 'Apertura',
      sortable: true,
      sortKey: 'fecha_apertura',
      width: '10%',
      type: 'date',
      render: (value) => formatDateDisplay(value)
    },
    {
      key: 'fecha_cierre_planificada',
      label: 'Planif.',
      sortable: true,
      sortKey: 'fecha_cierre_planificada',
      width: '95px',
      type: 'date',
      render: (value) => value ? formatDateDisplay(value) : '-'
    },
    {
      key: 'fecha_cierre',
      label: 'Cierre',
      sortable: true,
      sortKey: 'fecha_cierre',
      width: '10%',
      type: 'date',
      render: (value) => value ? formatDateDisplay(value) : '-'
    },
    {
      key: 'descripcion',
      label: 'Descripción',
      sortable: false,
      width: '18%',
      render: (value) => {
        if (!value) return '-';
        return value.length > 30 ? `${value.substring(0, 30)}...` : value;
      }
    }
  ];

  const handleAdd = () => {
    navigate('/reparaciones/otc/nueva');
  };

  const handleEdit = (otc) => {
    if (!otc?.id) return;
    navigate(`/reparaciones/otc/${otc.id}`);
  };

  const openLinkedOtc = useCallback((otcId) => {
    if (!otcId) return;
    navigate(`/reparaciones/otc/${otcId}`);
  }, [navigate]);

  const handleEliminar = async (otc) => {
    if (!otc?.id) return;
    const ok = await confirmar({
      titulo: `¿Eliminar OTC ${otc.numero_ot || ''}?`,
      mensaje: 'Esta acción no se puede deshacer. El registro será eliminado definitivamente.',
      labelConfirmar: 'Sí, eliminar',
      variante: 'danger',
    });
    if (!ok) return;
    try {
      await otcService.eliminar(otc.id);
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar la OTC.');
    }
  };

  const handleCancelar = async (otc) => {
    if (!otc || otc.estado !== 'abierta') return;
    const ok = await confirmar({
      titulo: `¿Cancelar OTC ${otc.numero_ot || ''}?`,
      mensaje: 'La OTC quedará bloqueada para edición.',
      labelConfirmar: 'Sí, cancelar',
      variante: 'danger',
    });
    if (!ok) return;
    try {
      await otcService.cancelar(otc.id, { fecha_cierre: today });
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cancelar la OTC.');
    }
  };

  const resetDetencionForm = useCallback(() => {
    setEditingDetencion(null);
    setOtcAbiertasEquipo([]);
    setDetencionForm({
      equipo: '',
      motivo_detencion: '',
      estado_equipo: '',
      motivo: 'otras',
      fecha_ingreso: today,
      fecha_egreso_prevista: '',
      fecha_egreso_real: '',
      observaciones: '',
      otcs_asociadas_ids: [],
    });
  }, [today]);

  const loadOtcAbiertasPorEquipo = useCallback(async (equipoId) => {
    if (!equipoId) {
      setOtcAbiertasEquipo([]);
      return [];
    }

    try {
      const data = await otcService.listar({
        page_size: 1000,
        ordering: '-fecha_apertura',
        'equipo__id': Number(equipoId),
        estado: 'abierta',
      });
      const abiertas = data?.results || data || [];
      setOtcAbiertasEquipo(abiertas);
      return abiertas;
    } catch (error) {
      console.error('Error cargando OTC abiertas por equipo:', error);
      setOtcAbiertasEquipo([]);
      return [];
    }
  }, []);

  const handleAddDetencion = useCallback(() => {
    resetDetencionForm();
    setShowDetencionModal(true);
  }, [resetDetencionForm]);

  const handleEditDetencion = useCallback(async (row) => {
    try {
      const detail = await detencionesService.obtener(row.id);
      const equipoId = detail.equipo ? String(detail.equipo) : '';
      setEditingDetencion(detail);
      setDetencionForm({
        equipo: equipoId,
        motivo_detencion: detail.motivo_detencion ? String(detail.motivo_detencion) : '',
        estado_equipo: detail.estado_equipo ? String(detail.estado_equipo) : '',
        motivo: detail.motivo || 'otras',
        fecha_ingreso: detail.fecha_ingreso || today,
        fecha_egreso_prevista: detail.fecha_egreso_prevista || '',
        fecha_egreso_real: detail.fecha_egreso_real || '',
        observaciones: detail.observaciones || '',
        otcs_asociadas_ids: (detail.otcs_asociadas || []).map((x) => x.id),
      });
      const abiertas = await loadOtcAbiertasPorEquipo(equipoId);
      setDetencionForm((prev) => ({
        ...prev,
        otcs_asociadas_ids: abiertas.map((otc) => otc.id),
      }));
      setShowDetencionModal(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo abrir la parada. Intentá nuevamente.');
    }
  }, [today, loadOtcAbiertasPorEquipo, toast]);

  const handleDeleteDetencion = useCallback(async (row) => {
    const ok = await confirmar({
      titulo: '¿Eliminar detención?',
      mensaje: `Equipo: ${row.equipo_interno || row.equipo}`,
      labelConfirmar: 'Sí, eliminar',
      variante: 'danger',
    });
    if (!ok) return;
    try {
      await detencionesService.eliminar(row.id);
      await loadDetenciones();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar la detencion.');
    }
  }, [loadDetenciones, confirmar, toast]);

  // ----------------------------- Stand by -----------------------------
  const resetStandByForm = useCallback(() => {
    setEditingStandBy(null);
    setStandByForm({ equipos: [], fecha_inicio: today, fecha_fin: '', motivo: '', observaciones: '' });
  }, [today]);

  const cerrarStandByModal = useCallback(() => {
    setShowStandByModal(false);
    resetStandByForm();
  }, [resetStandByForm]);

  const handleAddStandBy = useCallback(() => {
    resetStandByForm();
    setShowStandByModal(true);
  }, [resetStandByForm]);

  const handleEditStandBy = useCallback((row) => {
    setEditingStandBy(row);
    setStandByForm({
      equipos: [String(row.equipo)],
      fecha_inicio: row.fecha_inicio || today,
      fecha_fin: row.fecha_fin || '',
      motivo: row.motivo || '',
      observaciones: row.observaciones || '',
    });
    setShowStandByModal(true);
  }, [today]);

  const mensajeErrorStandBy = (e, porDefecto) => {
    const data = e?.response?.data;
    if (!data) return porDefecto;
    if (typeof data === 'string') return porDefecto;
    return data.detail || Object.values(data).flat().join(' ') || porDefecto;
  };

  const handleSaveStandBy = useCallback(async () => {
    const equipos = standByForm.equipos.map(Number).filter(Boolean);
    if (!equipos.length) {
      toast.warning('Seleccioná al menos un equipo.');
      return;
    }
    if (!standByForm.fecha_inicio) {
      toast.warning('Indicá desde cuándo está en stand by.');
      return;
    }
    if (standByForm.fecha_fin && standByForm.fecha_fin < standByForm.fecha_inicio) {
      toast.warning('La fecha de salida no puede ser anterior a la de inicio.');
      return;
    }
    const datos = {
      fecha_inicio: standByForm.fecha_inicio,
      fecha_fin: standByForm.fecha_fin || null,
      motivo: standByForm.motivo.trim(),
      observaciones: standByForm.observaciones.trim(),
    };
    setSavingStandBy(true);
    try {
      if (editingStandBy?.id) {
        await standByService.actualizar(editingStandBy.id, { ...datos, equipo: equipos[0] });
      } else if (equipos.length === 1) {
        await standByService.crear({ ...datos, equipo: equipos[0] });
      } else {
        const r = await standByService.entrarMasivo({ ...datos, equipos });
        if (r.ya_en_stand_by) {
          toast.info(`${r.creados} equipo${r.creados !== 1 ? 's' : ''} en stand by. ${r.ya_en_stand_by} ya lo estaba${r.ya_en_stand_by !== 1 ? 'n' : ''} y no se modificó.`);
        }
      }
      cerrarStandByModal();
      refetchStandBy();
    } catch (e) {
      toast.error(mensajeErrorStandBy(e, 'No se pudo guardar el stand by. Verificá los datos e intentá nuevamente.'));
    } finally {
      setSavingStandBy(false);
    }
  }, [standByForm, editingStandBy, cerrarStandByModal, refetchStandBy, toast]);

  const handleSalirStandBy = useCallback(async (row) => {
    const fechaIngresada = await entrada({
      titulo: 'Fecha de salida de stand by',
      label: 'Ingresá la fecha (DD-MM-YYYY)',
      tipo: 'text',
      valorInicial: formatDateDisplay(today),
      placeholder: 'DD-MM-YYYY',
    });
    if (!fechaIngresada) return;
    const fecha = normalizeDateInput(fechaIngresada);
    if (!fecha) {
      toast.warning('Formato de fecha invalido. Use DD-MM-YYYY.');
      return;
    }
    if (isDateAfterToday(fecha, today)) {
      toast.warning('La fecha de salida no puede ser posterior a hoy.');
      return;
    }
    try {
      await standByService.salir(row.id, { fecha_fin: fecha });
      if (editingStandBy?.id === row.id) cerrarStandByModal();
      refetchStandBy();
    } catch (e) {
      toast.error(mensajeErrorStandBy(e, 'No se pudo registrar la salida. Intentá nuevamente.'));
    }
  }, [today, entrada, toast, editingStandBy, cerrarStandByModal, refetchStandBy]);

  const handleReabrirStandBy = useCallback(async (row) => {
    try {
      await standByService.reabrir(row.id);
      if (editingStandBy?.id === row.id) cerrarStandByModal();
      refetchStandBy();
    } catch (e) {
      toast.error(mensajeErrorStandBy(e, 'No se pudo reabrir el stand by. Intentá nuevamente.'));
    }
  }, [toast, editingStandBy, cerrarStandByModal, refetchStandBy]);

  const handleDeleteStandBy = useCallback(async (row) => {
    const ok = await confirmar({
      titulo: '¿Eliminar registro de stand by?',
      mensaje: `Equipo: ${row.equipo_interno || row.equipo}. Se pierde del historial del equipo.`,
      labelConfirmar: 'Sí, eliminar',
      variante: 'danger',
    });
    if (!ok) return;
    try {
      await standByService.eliminar(row.id);
      refetchStandBy();
    } catch (e) {
      toast.error(mensajeErrorStandBy(e, 'No se pudo eliminar el registro.'));
    }
  }, [confirmar, toast, refetchStandBy]);

  useEffect(() => {
    if (!showDetencionModal) return;

    let active = true;
    const syncOtcsAbiertas = async () => {
      if (!detencionForm.equipo) {
        if (!active) return;
        setDetencionForm((prev) => ({ ...prev, otcs_asociadas_ids: [] }));
        return;
      }
      const abiertas = await loadOtcAbiertasPorEquipo(detencionForm.equipo);
      if (!active) return;
      setDetencionForm((prev) => ({
        ...prev,
        otcs_asociadas_ids: abiertas.map((otc) => otc.id),
      }));
    };

    syncOtcsAbiertas();
    return () => {
      active = false;
    };
  }, [showDetencionModal, detencionForm.equipo, loadOtcAbiertasPorEquipo]);

  const handleSaveDetencion = useCallback(async () => {
    if (
      !detencionForm.equipo
      || !detencionForm.motivo_detencion
      || !detencionForm.estado_equipo
      || !detencionForm.fecha_ingreso
      || !detencionForm.fecha_egreso_prevista
    ) {
      toast.warning('Equipo, motivo, estado de equipo, fecha ingreso y fecha egreso prevista son obligatorios.');
      return;
    }

    if (detencionForm.fecha_egreso_real && isDateAfterToday(detencionForm.fecha_egreso_real, today)) {
      toast.warning('La fecha de egreso real no puede ser posterior a hoy.');
      return;
    }

    const abiertas = await loadOtcAbiertasPorEquipo(detencionForm.equipo);
    const payload = {
      ...detencionForm,
      equipo: Number(detencionForm.equipo),
      motivo_detencion: Number(detencionForm.motivo_detencion),
      estado_equipo: Number(detencionForm.estado_equipo),
      motivo: 'otras',
      fecha_egreso_real: detencionForm.fecha_egreso_real || null,
      observaciones: detencionForm.observaciones?.trim() || null,
      otcs_asociadas_ids: abiertas.map((otc) => otc.id),
    };
    setSavingDetencion(true);
    try {
      if (editingDetencion?.id) {
        await detencionesService.actualizar(editingDetencion.id, payload);
      } else {
        await detencionesService.crear(payload);
      }
      setShowDetencionModal(false);
      resetDetencionForm();
      await loadDetenciones();
    } catch (e) {
      const data = e?.response?.data;
      const msg = data?.detail
        || data?.equipo?.[0]
        || data?.otcs_asociadas_ids?.[0]
        || 'No se pudo guardar la parada. Verificá los datos e intentá nuevamente.';
      toast.error(msg);
    } finally {
      setSavingDetencion(false);
    }
  }, [detencionForm, editingDetencion, resetDetencionForm, loadDetenciones, loadOtcAbiertasPorEquipo, today, toast]);

  const handleCompletarDetencion = useCallback(async (row) => {
    const fechaIngresada = await entrada({
      titulo: 'Fecha de reparación real',
      label: 'Ingresá la fecha (DD-MM-YYYY)',
      tipo: 'text',
      valorInicial: formatDateDisplay(today),
      placeholder: 'DD-MM-YYYY',
    });
    if (!fechaIngresada) return;
    const fecha = normalizeDateInput(fechaIngresada);
    if (!fecha) {
      toast.warning('Formato de fecha invalido. Use DD-MM-YYYY.');
      return;
    }
    if (isDateAfterToday(fecha, today)) {
      toast.warning('La fecha de reparacion real no puede ser posterior a hoy.');
      return;
    }
    try {
      await detencionesService.egresar(row.id, { fecha_egreso_real: fecha });
      await loadDetenciones();
      if (editingDetencion?.id === row.id) {
        setShowDetencionModal(false);
        resetDetencionForm();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo registrar el egreso. Intentá nuevamente.');
    }
  }, [today, loadDetenciones, editingDetencion, resetDetencionForm, entrada, toast]);

  const handleReabrirDetencion = useCallback(async (row) => {
    try {
      await detencionesService.reabrir(row.id);
      await loadDetenciones();
      if (editingDetencion?.id === row.id) {
        setShowDetencionModal(false);
        resetDetencionForm();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo reabrir la parada. Intentá nuevamente.');
    }
  }, [loadDetenciones, editingDetencion, resetDetencionForm, toast]);

  const activeFiltersCount = useMemo(() => {
    const allowedKeys = new Set(filterColumns.map((column) => column.key));
    const dynamicCount = Object.entries(dynamicFilters?.touchedFilters || {}).reduce((acc, [key, touched]) => {
      if (!touched) return acc;
      if (!allowedKeys.has(key)) return acc;
      return acc + ensureArray(dynamicFilters?.columnFilters?.[key]).length;
    }, 0);

    const dateCount = (filters.fecha_desde ? 1 : 0) + (filters.fecha_hasta ? 1 : 0);
    return dynamicCount + dateCount;
  }, [dynamicFilters, filterColumns, filters.fecha_desde, filters.fecha_hasta]);

  const columnsWithActions = [
    ...columns,
    {
      key: 'acciones',
      label: 'Acciones',
      sortable: false,
      width: '96px',
      noResize: true,
      align: 'center',
      headerAlign: 'center',
      render: (_, row) => (
        <RowActions>
          <ActionButton
            $variant="edit"
            title="Editar OTC"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row);
            }}
          >
            <i className="bi bi-pencil" />
          </ActionButton>
          {row.estado === 'abierta' ? (
            <ActionButton
              $variant="danger"
              title="Cancelar OTC"
              onClick={(e) => {
                e.stopPropagation();
                handleCancelar(row);
              }}
            >
              <i className="bi bi-x-circle" />
            </ActionButton>
          ) : canDeleteOtc ? (
            <ActionButton
              $variant="danger"
              title="Eliminar OTC del historial"
              onClick={(e) => {
                e.stopPropagation();
                handleEliminar(row);
              }}
            >
              <i className="bi bi-trash" />
            </ActionButton>
          ) : null}
        </RowActions>
      )
    }
  ];

  const filteredByInfra = useMemo(() => {
    const norm = (v) => String(v || '').toLowerCase();
    const q = norm(filters.q);
    const selected = dynamicFilters.columnFilters || {};
    const touched = dynamicFilters.touchedFilters || {};

    const matchDynamic = (row, key) => {
      if (!touched[key]) return true;
      const selectedValues = ensureArray(selected[key]);
      if (!selectedValues.length) return false;
      return selectedValues.includes(getInfraFilterValue(row, key));
    };

    return rows.filter((r) => {
      const eq = equipoMap[r.equipo] || {};
      if (!matchDynamic(r, 'sucursal')) return false;
      if (!matchDynamic(r, 'familia')) return false;
      if (!matchDynamic(r, 'tipo')) return false;
      if (filters.fecha_desde && String(r.fecha_apertura || '') < filters.fecha_desde) return false;
      if (filters.fecha_hasta && String(r.fecha_apertura || '') > filters.fecha_hasta) return false;
      if (q) {
        const haystack = [
          r.numero_ot,
          r.titulo,
          r.descripcion,
          r.equipo_interno,
          r.equipo_interno_alternativo || eq.interno_alternativo,
          r.mecanico_responsable_nombre,
          eq.descripcion,
          eq.sucursal_nombre,
          eq.familia_nombre,
          eq.tipo_nombre,
        ].map(norm).join(' ');
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filters, dynamicFilters, equipoMap, getInfraFilterValue]);

  // Base de detenciones sin filtro de estado — para los cards (igual que filteredByInfra para OTCs).
  const detencionesBase = useMemo(() => {
    const norm = (v) => String(v || '').toLowerCase();
    const q = norm(filters.q);
    const selected = dynamicFilters.columnFilters || {};
    const touched = dynamicFilters.touchedFilters || {};
    const matchDynamic = (row, key) => {
      if (!touched[key]) return true;
      const vals = ensureArray(selected[key]);
      if (!vals.length) return false;
      return vals.includes(getInfraFilterValue(row, key));
    };
    return detencionesRows.filter((r) => {
      if (!matchDynamic(r, 'sucursal')) return false;
      if (!matchDynamic(r, 'familia')) return false;
      if (!matchDynamic(r, 'tipo')) return false;
      if (filters.fecha_desde && String(r.fecha_ingreso || '') < filters.fecha_desde) return false;
      if (filters.fecha_hasta && String(r.fecha_ingreso || '') > filters.fecha_hasta) return false;
      if (q) {
        const haystack = [
          r.equipo_interno,
          r.equipo_interno_alternativo || equipoMap[r.equipo]?.interno_alternativo,
          r.equipo_descripcion,
          r.motivo_detencion_nombre,
          r.estado_equipo_nombre,
          r.observaciones,
        ].map(norm).join(' ');
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [detencionesRows, filters, dynamicFilters, equipoMap, getInfraFilterValue]);

  const filteredDetencionesRows = useMemo(() => {
    const selected = dynamicFilters.columnFilters || {};
    const touched = dynamicFilters.touchedFilters || {};
    const matchEstado = (row) => {
      if (!touched.estado) return true;
      const vals = ensureArray(selected.estado);
      if (!vals.length) return false;
      return vals.includes(getInfraFilterValue(row, 'estado'));
    };
    const arr = detencionesBase.filter(matchEstado);
    if (detencionesSortColumn) {
      arr.sort((a, b) => {
        const va = a?.[detencionesSortColumn];
        const vb = b?.[detencionesSortColumn];
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        const sa = String(va).toLowerCase();
        const sb = String(vb).toLowerCase();
        if (sa < sb) return detencionesSortDirection === 'asc' ? -1 : 1;
        if (sa > sb) return detencionesSortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return arr;
  }, [detencionesBase, detencionesSortColumn, detencionesSortDirection, dynamicFilters, getInfraFilterValue]);

  const standByBase = useMemo(() => {
    const norm = (v) => String(v || '').toLowerCase();
    const q = norm(filters.q);
    const selected = dynamicFilters.columnFilters || {};
    const touched = dynamicFilters.touchedFilters || {};
    const matchDynamic = (row, key) => {
      if (!touched[key]) return true;
      const vals = ensureArray(selected[key]);
      if (!vals.length) return false;
      return vals.includes(getInfraFilterValue(row, key));
    };
    return standByRows.filter((r) => {
      for (const key of ['sucursal', 'familia', 'tipo', 'marca', 'modelo', 'equipo', 'motivo']) {
        if (!matchDynamic(r, key)) return false;
      }
      if (filters.fecha_desde && String(r.fecha_inicio || '') < filters.fecha_desde) return false;
      if (filters.fecha_hasta && String(r.fecha_inicio || '') > filters.fecha_hasta) return false;
      if (q) {
        const haystack = [r.equipo_interno, r.equipo_interno_alternativo || equipoMap[r.equipo]?.interno_alternativo, r.equipo_descripcion, r.motivo, r.observaciones].map(norm).join(' ');
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [standByRows, filters, dynamicFilters, equipoMap, getInfraFilterValue]);

  // Días acumulados en stand by por equipo (todos sus períodos, sin filtros):
  // es el histórico del equipo, no el de la vista.
  const diasStandByPorEquipo = useMemo(() => {
    const acc = {};
    standByRows.forEach((r) => { acc[r.equipo] = (acc[r.equipo] || 0) + (Number(r.dias) || 0); });
    return acc;
  }, [standByRows]);

  const filteredStandByRows = useMemo(() => {
    const selected = dynamicFilters.columnFilters || {};
    const touched = dynamicFilters.touchedFilters || {};
    const matchEstado = (row) => {
      if (!touched.estado) return true;
      const vals = ensureArray(selected.estado);
      if (!vals.length) return false;
      return vals.includes(getInfraFilterValue(row, 'estado'));
    };
    const valor = (row, col) => (col === 'dias_equipo' ? diasStandByPorEquipo[row.equipo] : row?.[col]);
    const numerica = standBySortColumn === 'dias' || standBySortColumn === 'dias_equipo';
    const arr = standByBase.filter(matchEstado);
    if (standBySortColumn) {
      arr.sort((a, b) => {
        const va = valor(a, standBySortColumn);
        const vb = valor(b, standBySortColumn);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        const sa = numerica ? Number(va) : String(va).toLowerCase();
        const sb = numerica ? Number(vb) : String(vb).toLowerCase();
        if (sa < sb) return standBySortDirection === 'asc' ? -1 : 1;
        if (sa > sb) return standBySortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return arr;
  }, [standByBase, standBySortColumn, standBySortDirection, dynamicFilters, getInfraFilterValue, diasStandByPorEquipo]);

  const stats = useMemo(() => {
    const abiertas = filteredByInfra.filter((r) => r.estado === 'abierta');
    const cerradas = filteredByInfra.filter((r) => r.estado === 'cerrada').length;
    const canceladas = filteredByInfra.filter((r) => r.estado === 'cancelada').length;
    const programadas = abiertas.filter((r) => r.fecha_cierre_planificada && r.fecha_cierre_planificada >= today).length;
    const atrasadas = abiertas.filter((r) => r.fecha_cierre_planificada && r.fecha_cierre_planificada < today).length;
    const sin_planificacion = abiertas.filter((r) => !r.fecha_cierre_planificada).length;

    return {
      abiertas: abiertas.length,
      cerradas,
      canceladas,
      programadas,
      atrasadas,
      sin_planificacion,
      fuera_servicio: detencionesBase.filter((d) => d.estado === 'en_parada').length,
      total_detenciones: detencionesBase.length,
      stand_by: standByBase.filter((r) => r.esta_activo).length,
    };
  }, [filteredByInfra, today, detencionesBase, standByBase]);

  const filteredRows = useMemo(() => {
    const baseByTab = filteredByInfra.filter((r) => {
      if (tab === 'activas') return r.estado === 'abierta';
      return r.estado === 'cerrada' || r.estado === 'cancelada';
    });

    const byFilters = baseByTab.filter((r) => {
      if (tab !== 'fuera_servicio') {
        const selectedEstado = ensureArray(dynamicFilters.columnFilters?.estado);
        const touchedEstado = !!dynamicFilters.touchedFilters?.estado;
        if (touchedEstado) {
          if (!selectedEstado.length) return false;
          if (!selectedEstado.includes(getInfraFilterValue(r, 'estado'))) return false;
        }
      }

      if (tab === 'activas') {
        return true;
      } else {
        return true;
      }
    });

    const arr = byFilters.slice();
    if (sortColumn) {
      arr.sort((a, b) => {
        const va = a?.[sortColumn];
        const vb = b?.[sortColumn];
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (typeof va === 'number' && typeof vb === 'number') {
          return sortDirection === 'asc' ? va - vb : vb - va;
        }
        const sa = String(va).toLowerCase();
        const sb = String(vb).toLowerCase();
        if (sa < sb) return sortDirection === 'asc' ? -1 : 1;
        if (sa > sb) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return arr;
  }, [filteredByInfra, tab, dynamicFilters, sortColumn, sortDirection, getInfraFilterValue]);

  const detencionesColumnsWithActions = useMemo(() => ([
    {
      key: 'equipo_interno',
      label: 'Equipo',
      sortable: true,
      width: '120px',
      render: (value, item) => (
        <LinkEquipo
          href={`/?equipoId=${item.equipo}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.location.href = `/?equipoId=${item.equipo}`;
          }}
        >
          {value || '-'}
        </LinkEquipo>
      )
    },
    { key: 'equipo_descripcion', label: 'Descripcion', sortable: true, width: '220px' },
    {
      key: 'motivo_detencion_nombre',
      label: 'Motivo',
      sortable: true,
      width: '180px',
      render: (_v, row) => row.motivo_detencion_nombre || row.motivo_display || '-',
    },
    {
      key: 'estado_equipo_nombre',
      label: 'Estado Equipo',
      sortable: true,
      width: '170px',
      render: (v) => v || '-',
    },
    {
      key: 'estado_display',
      label: 'Detencion',
      sortable: true,
      width: '120px',
      render: (_v, row) => row.estado === 'en_parada'
        ? <UnifiedStatusBadge $tone="danger">FUERA DE SERVICIO</UnifiedStatusBadge>
        : <UnifiedStatusBadge $tone="success">REPARADO</UnifiedStatusBadge>
    },
    { key: 'fecha_ingreso', label: 'Ingreso', sortable: true, type: 'date', width: '95px', render: (v) => formatDateDisplay(v) },
    { key: 'fecha_egreso_prevista', label: 'Egreso Prev.', sortable: true, type: 'date', width: '105px', render: (v) => v ? formatDateDisplay(v) : '-' },
    { key: 'fecha_egreso_real', label: 'Egreso Real', sortable: true, type: 'date', width: '105px', render: (v) => v ? formatDateDisplay(v) : '-' },
    { key: 'dias_parada', label: 'Dias', sortable: true, width: '70px' },
    {
      key: 'otcs_asociadas',
      label: 'OTC vinculadas',
      sortable: false,
      width: '200px',
      allowWrap: true,
      render: (value, row) => {
        const otcsAsociadas = Array.isArray(value) ? value : [];
        const otcsAbiertasEquipo = (rows || [])
          .filter((otc) => String(otc.equipo) === String(row?.equipo) && otc.estado === 'abierta')
          .map((otc) => ({
            id: otc.id,
            numero_ot: otc.numero_ot,
            titulo: otc.titulo,
          }));

        const mergedMap = new Map();
        [...otcsAsociadas, ...otcsAbiertasEquipo].forEach((otc) => {
          if (!otc?.id) return;
          mergedMap.set(otc.id, otc);
        });
        const otcs = Array.from(mergedMap.values());
        if (!otcs.length) return '-';

        return (
          <OtcLinksStack>
            {otcs.map((ot) => (
              <OtcInlineLink
                key={ot.id}
                title={ot.titulo || 'Abrir OTC'}
                onClick={(e) => {
                  e.stopPropagation();
                  openLinkedOtc(ot.id);
                }}
              >
                {ot.numero_ot || `OTC-${ot.id}`}
              </OtcInlineLink>
            ))}
          </OtcLinksStack>
        );
      }
    },
    {
      key: 'acciones',
      label: 'Acciones',
      sortable: false,
      width: '140px',
      noResize: true,
      align: 'center',
      headerAlign: 'center',
      render: (_, row) => (
        <RowActions>
          <ActionButton
            $variant="edit"
            title="Editar"
            onClick={(e) => {
              e.stopPropagation();
              handleEditDetencion(row);
            }}
          >
            <i className="bi bi-pencil" />
          </ActionButton>
          {row.estado === 'en_parada' ? (
            <ActionButton
              $variant="success"
              title="Completar"
              onClick={(e) => {
                e.stopPropagation();
                handleCompletarDetencion(row);
              }}
            >
              <i className="bi bi-check-circle" />
            </ActionButton>
          ) : (
            <ActionButton
              $variant="warning"
              title="Reabrir"
              onClick={(e) => {
                e.stopPropagation();
                handleReabrirDetencion(row);
              }}
            >
              <i className="bi bi-arrow-counterclockwise" />
            </ActionButton>
          )}
          <ActionButton
            $variant="danger"
            title="Eliminar"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteDetencion(row);
            }}
          >
            <i className="bi bi-x-circle" />
          </ActionButton>
        </RowActions>
      )
    }
  ]), [handleCompletarDetencion, handleDeleteDetencion, handleEditDetencion, handleReabrirDetencion, openLinkedOtc, rows]);

  const standByColumnsWithActions = useMemo(() => ([
    {
      key: 'equipo_interno',
      label: 'Equipo',
      sortable: true,
      width: '120px',
      render: (value, item) => (
        <LinkEquipo
          href={`/?equipoId=${item.equipo}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.location.href = `/?equipoId=${item.equipo}`;
          }}
        >
          {value || '-'}
        </LinkEquipo>
      )
    },
    { key: 'equipo_descripcion', label: 'Descripcion', sortable: true, width: '220px' },
    { key: 'motivo', label: 'Motivo', sortable: true, width: '200px', render: (v) => v || '-' },
    {
      key: 'esta_activo',
      label: 'Estado',
      sortable: true,
      width: '120px',
      render: (_v, row) => row.esta_activo
        ? <UnifiedStatusBadge $tone="info">EN STAND BY</UnifiedStatusBadge>
        : <UnifiedStatusBadge $tone="success">FINALIZADO</UnifiedStatusBadge>
    },
    { key: 'fecha_inicio', label: 'Desde', sortable: true, type: 'date', width: '95px', render: (v) => formatDateDisplay(v) },
    { key: 'fecha_fin', label: 'Hasta', sortable: true, type: 'date', width: '95px', render: (v) => v ? formatDateDisplay(v) : '-' },
    { key: 'dias', label: 'Dias', sortable: true, width: '70px' },
    {
      key: 'dias_equipo',
      label: 'Total equipo',
      sortable: true,
      width: '100px',
      render: (_v, row) => diasStandByPorEquipo[row.equipo] ?? 0,
    },
    {
      key: 'acciones',
      label: 'Acciones',
      sortable: false,
      width: '140px',
      noResize: true,
      align: 'center',
      headerAlign: 'center',
      render: (_, row) => (
        <RowActions>
          <ActionButton
            $variant="edit"
            title="Editar"
            onClick={(e) => {
              e.stopPropagation();
              handleEditStandBy(row);
            }}
          >
            <i className="bi bi-pencil" />
          </ActionButton>
          {row.esta_activo ? (
            <ActionButton
              $variant="success"
              title="Registrar salida"
              onClick={(e) => {
                e.stopPropagation();
                handleSalirStandBy(row);
              }}
            >
              <i className="bi bi-play-circle" />
            </ActionButton>
          ) : (
            <ActionButton
              $variant="warning"
              title="Reabrir"
              onClick={(e) => {
                e.stopPropagation();
                handleReabrirStandBy(row);
              }}
            >
              <i className="bi bi-arrow-counterclockwise" />
            </ActionButton>
          )}
          <ActionButton
            $variant="danger"
            title="Eliminar"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteStandBy(row);
            }}
          >
            <i className="bi bi-x-circle" />
          </ActionButton>
        </RowActions>
      )
    }
  ]), [diasStandByPorEquipo, handleEditStandBy, handleSalirStandBy, handleReabrirStandBy, handleDeleteStandBy]);

  const opcionesEquiposStandBy = useMemo(() => Object.values(equipoMap)
    .filter((eq) => !eq.baja)
    .sort((a, b) => String(a.interno || '').localeCompare(String(b.interno || '')))
    .map((eq) => ({ value: String(eq.id), label: `${eq.interno || `EQ-${eq.id}`} - ${eq.descripcion || '-'}` })),
  [equipoMap]);

  const esTabStandBy = tab === 'stand_by';
  const esTabFS = tab === 'fuera_servicio';

  return (
    <PageShell>
      <Container>
        <ModuloHeader
          icono="bi bi-tools"
          titulo="Reparaciones"
          descripcion="Ordenes de trabajo de reparaciones correctivas, equipos fuera de servicio y equipos en stand by."
          help={HELP}
          helpOpen={helpOpen}
          onToggleHelp={() => setHelpOpen(v => !v)}
          tabs={TABS}
          tabActiva={tab}
          onTabChange={(key) => { setTab(key); clearEstadoFilter(); }}
          stats={(
            <>
            <ClickableStatCard $active={isCardActive('abierta')} onClick={() => applyCardFilter('abierta')}>
              <span className="icon" style={{ color: theme.colors.violeta }}><i className="bi bi-folder2-open" /></span>
              <span className="label">Abiertas</span>
              <span className="value">{stats.abiertas}</span>
            </ClickableStatCard>
            <ClickableStatCard $active={isCardActive('programada')} onClick={() => applyCardFilter('programada')}>
              <span className="icon" style={{ color: theme.colors.info }}><i className="bi bi-calendar2-check" /></span>
              <span className="label">PROGRAMADAS</span>
              <span className="value">{stats.programadas}</span>
            </ClickableStatCard>
            <ClickableStatCard $active={isCardActive('atrasada')} onClick={() => applyCardFilter('atrasada')}>
              <span className="icon" style={{ color: theme.colors.danger }}><i className="bi bi-exclamation-triangle" /></span>
              <span className="label">ATRASADAS</span>
              <span className="value">{stats.atrasadas}</span>
            </ClickableStatCard>
            <ClickableStatCard $active={isCardActive('sin_planificacion')} onClick={() => applyCardFilter('sin_planificacion')}>
              <span className="icon" style={{ color: theme.colors.secondary }}><i className="bi bi-calendar-x" /></span>
              <span className="label">Sin Planif.</span>
              <span className="value">{stats.sin_planificacion}</span>
            </ClickableStatCard>
            <ClickableStatCard $active={isCardActive('cerrada')} onClick={() => applyCardFilter('cerrada')}>
              <span className="icon" style={{ color: theme.colors.success }}><i className="bi bi-check2-circle" /></span>
              <span className="label">CERRADAS</span>
              <span className="value">{stats.cerradas}</span>
            </ClickableStatCard>
            <ClickableStatCard $active={isCardActive('cancelada')} onClick={() => applyCardFilter('cancelada')}>
              <span className="icon" style={{ color: theme.colors.warning }}><i className="bi bi-slash-circle" /></span>
              <span className="label">CANCELADAS</span>
              <span className="value">{stats.canceladas}</span>
            </ClickableStatCard>
            <ClickableStatCard $active={isCardActive('fuera_servicio')} onClick={() => applyCardFilter('fuera_servicio')}>
              <span className="icon" style={{ color: theme.colors.danger }}><i className="bi bi-cone-striped" /></span>
              <span className="label">Equipos F/S</span>
              <span className="value">{stats.fuera_servicio}</span>
            </ClickableStatCard>
            <ClickableStatCard $active={isCardActive('todas_detenciones')} onClick={() => applyCardFilter('todas_detenciones')}>
              <span className="icon" style={{ color: theme.colors.txtSecondary }}><i className="bi bi-clipboard2-pulse" /></span>
              <span className="label">Detenciones</span>
              <span className="value">{stats.total_detenciones}</span>
            </ClickableStatCard>
            <ClickableStatCard $active={isCardActive('stand_by')} onClick={() => applyCardFilter('stand_by')}>
              <span className="icon" style={{ color: theme.colors.info }}><i className="bi bi-pause-circle" /></span>
              <span className="label">Equipos Stand by</span>
              <span className="value">{stats.stand_by}</span>
            </ClickableStatCard>
            </>
          )}
        />

        <SeccionBar>
          <SeccionTitulo>
            {tab === 'activas'
              ? 'OT Correctivas'
              : tab === 'historial'
                ? 'Historial de Órdenes Correctivas'
                : esTabStandBy ? 'Equipos en Stand by' : 'Equipos Fuera de Servicio'}
          </SeccionTitulo>
          <SeccionAcciones>
            <SeccionBuscador
              placeholder={esTabStandBy ? 'Buscar equipo, motivo...' : (esTabFS ? 'Buscar equipo, motivo, estado...' : 'Buscar OT, equipo, título...')}
              value={filters.q}
              onChange={(q) => setFilters((v) => ({ ...v, q }))}
            />
            <OutlinedButton $size="sm" style={ESTILO_BOTON_SECCION} onClick={() => setShowFiltersModal(true)}>
              <i className="bi bi-funnel" style={{ fontSize: 14 }} /> Filtros {activeFiltersCount ? `(${activeFiltersCount})` : ''}
            </OutlinedButton>
            <OutlinedButton
              $variant="secondary"
              $size="sm"
              style={ESTILO_BOTON_SECCION}
              onClick={() => {
                setFilters({ q: '', fecha_desde: '', fecha_hasta: '' });
                setDynamicFilters({ columnFilters: {}, touchedFilters: {}, expandedFilterGroups: {} });
              }}
            >
              <i className="bi bi-eraser" style={{ fontSize: 14 }} /> Limpiar
            </OutlinedButton>
            {esTabStandBy ? (
              <OutlinedButton $variant="success" $size="sm" style={ESTILO_BOTON_SECCION} onClick={handleAddStandBy} title="Nuevo stand by">
                <i className="bi bi-plus-circle" /> Nuevo Stand by
              </OutlinedButton>
            ) : esTabFS ? (
              <OutlinedButton $variant="success" $size="sm" style={ESTILO_BOTON_SECCION} onClick={handleAddDetencion} title="Nueva detencion">
                <i className="bi bi-plus-circle" /> Nueva Detencion
              </OutlinedButton>
            ) : (
              <OutlinedButton $variant="success" $size="sm" style={ESTILO_BOTON_SECCION} onClick={handleAdd} title="Nueva OTC">
                <i className="bi bi-plus-circle" /> Nueva OTC
              </OutlinedButton>
            )}
          </SeccionAcciones>
        </SeccionBar>

        {(() => {
          const chips = [];
          filterColumns.forEach((column) => {
            if (!dynamicFilters.touchedFilters?.[column.key]) return;
            const selectedValues = ensureArray(dynamicFilters.columnFilters?.[column.key]);
            if (!selectedValues.length) {
              chips.push(
                <Chip key={`chip-${column.key}`}>
                  {column.label}: ninguno
                  <ChipButtonX onClick={() => {
                    setDynamicFilters((prev) => {
                      const nextFilters = { ...(prev.columnFilters || {}) };
                      const nextTouched = { ...(prev.touchedFilters || {}) };
                      delete nextFilters[column.key];
                      delete nextTouched[column.key];
                      return { ...prev, columnFilters: nextFilters, touchedFilters: nextTouched };
                    });
                  }}>×</ChipButtonX>
                </Chip>
              );
              return;
            }

            chips.push(
              <Chip key={`chip-${column.key}`}>
                {column.label}: {selectedValues.length} seleccionado/s
                <ChipButtonX onClick={() => {
                  setDynamicFilters((prev) => {
                    const nextFilters = { ...(prev.columnFilters || {}) };
                    const nextTouched = { ...(prev.touchedFilters || {}) };
                    delete nextFilters[column.key];
                    delete nextTouched[column.key];
                    return { ...prev, columnFilters: nextFilters, touchedFilters: nextTouched };
                  });
                }}>×</ChipButtonX>
              </Chip>
            );
          });

          if (filters.fecha_desde) chips.push(
            <Chip key="desde">Desde: {filters.fecha_desde} <ChipButtonX onClick={() => setFilters((f) => ({ ...f, fecha_desde: '' }))}>×</ChipButtonX></Chip>
          );
          if (filters.fecha_hasta) chips.push(
            <Chip key="hasta">Hasta: {filters.fecha_hasta} <ChipButtonX onClick={() => setFilters((f) => ({ ...f, fecha_hasta: '' }))}>×</ChipButtonX></Chip>
          );
          return chips.length ? <ChipsRow>{chips}</ChipsRow> : null;
        })()}

        {(esTabStandBy ? (hidratandoSb && !completoSb) : esTabFS ? (hidratandoDet && !completoDet) : (hidratandoOtc && !completoOtc && !loading)) && (
          <HydrationNote>
            <i className="bi bi-arrow-repeat" />
            Cargando registros en segundo plano…
            {tab !== 'fuera_servicio' && progresoOtc.total ? ` ${progresoOtc.cargados} de ${progresoOtc.total}` : ''}
            {' '}— los filtros se completan al terminar.
          </HydrationNote>
        )}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          paddingRight: '8px',
          paddingBottom: '0px'
        }}>
          <Table
            columns={esTabStandBy ? standByColumnsWithActions : (esTabFS ? detencionesColumnsWithActions : columnsWithActions)}
            data={esTabStandBy ? filteredStandByRows : (esTabFS ? filteredDetencionesRows : filteredRows)}
            loading={loading}
            tableVisualVariant="rows"
            emptyMessage={esTabStandBy ? 'No hay registros de stand by' : (esTabFS ? 'No hay equipos fuera de servicio' : (tab === 'activas' ? 'No hay OTC activas' : 'No hay OTC en historial'))}
            onSort={(field, direction) => {
              if (esTabStandBy) {
                setStandBySortColumn(field);
                setStandBySortDirection(direction);
              } else if (esTabFS) {
                setDetencionesSortColumn(field);
                setDetencionesSortDirection(direction);
              } else {
                setSortColumn(field);
                setSortDirection(direction);
              }
            }}
            sortColumn={esTabStandBy ? standBySortColumn : (esTabFS ? detencionesSortColumn : sortColumn)}
            sortDirection={esTabStandBy ? standBySortDirection : (esTabFS ? detencionesSortDirection : sortDirection)}
            showActions={false}
            resizableColumns
            noExtraBottomSpace={true}
            columnSizingKey={esTabStandBy ? 'stand-by-lista' : (esTabFS ? 'detenciones-lista' : 'otc-lista')}
            horizontalScroll
          />
        </div>

        {showDetencionModal && (
          <Modal title={editingDetencion ? `Editar Detencion #${editingDetencion.id}` : 'Nueva Detencion'} onClose={() => { setShowDetencionModal(false); resetDetencionForm(); }} size="lg">
            <ModalGrid>
              <Field>
                <label>Equipo</label>
                <SearchableSelectField
                  options={Object.values(equipoMap)}
                  value={detencionForm.equipo}
                  onChange={(nextEquipoId) => setDetencionForm((v) => ({ ...v, equipo: nextEquipoId }))}
                  placeholder="Buscar equipo por interno o descripcion..."
                  emptyMessage="Sin equipos"
                  getOptionValue={(eq) => String(eq.id)}
                  getOptionLabel={(eq) => `${eq.interno || `EQ-${eq.id}`} - ${eq.descripcion || '-'}`}
                  getOptionSearch={(eq) => `${eq.interno || ''} ${eq.interno_alternativo || ''} ${eq.descripcion || ''} ${eq.marca_nombre || ''} ${eq.modelo_nombre || ''}`}
                />
              </Field>
              <Field>
                <label>Motivo</label>
                <FieldWithAdd>
                  <SearchableSelectField
                    options={motivosOpc}
                    value={detencionForm.motivo_detencion}
                    onChange={(nextMotivoId) => setDetencionForm((v) => ({ ...v, motivo_detencion: nextMotivoId }))}
                    placeholder="Buscar motivo de detención..."
                    emptyMessage="Sin motivos configurados"
                    getOptionValue={(motivo) => String(motivo.id)}
                    getOptionLabel={(motivo) => motivo.nombre || `Motivo ${motivo.id}`}
                  />
                  <AddFkButton tabIndex={-1} type="button" title="Agregar motivo" onClick={() => openQuickCreateModal('motivo')}>
                    +
                  </AddFkButton>
                </FieldWithAdd>
              </Field>
              <Field>
                <label>Fecha ingreso</label>
                <input type="date" value={detencionForm.fecha_ingreso} onChange={(e) => setDetencionForm((v) => ({ ...v, fecha_ingreso: e.target.value }))} />
              </Field>
              <Field>
                <label>Fecha egreso prevista</label>
                <input type="date" value={detencionForm.fecha_egreso_prevista} onChange={(e) => setDetencionForm((v) => ({ ...v, fecha_egreso_prevista: e.target.value }))} />
              </Field>
              <Field>
                <label>Estado de Equipo</label>
                <FieldWithAdd>
                  <SearchableSelectField
                    options={estadosEquipoOpc}
                    value={detencionForm.estado_equipo}
                    onChange={(nextEstadoId) => setDetencionForm((v) => ({ ...v, estado_equipo: nextEstadoId }))}
                    placeholder="Buscar estado de equipo..."
                    emptyMessage="Sin estados configurados"
                    getOptionValue={(estado) => String(estado.id)}
                    getOptionLabel={(estado) => estado.nombre || `Estado ${estado.id}`}
                  />
                  <AddFkButton tabIndex={-1} type="button" title="Agregar estado de equipo" onClick={() => openQuickCreateModal('estado')}>
                    +
                  </AddFkButton>
                </FieldWithAdd>
              </Field>
              <Field>
                <label>Fecha egreso real</label>
                <input type="date" max={today} value={detencionForm.fecha_egreso_real || ''} onChange={(e) => setDetencionForm((v) => ({ ...v, fecha_egreso_real: e.target.value }))} />
              </Field>
              <Field style={{ gridColumn: '1 / -1' }}>
                <label>OTC vinculadas automaticamente (abiertas del equipo)</label>
                <AutoInfo>
                  {detencionForm.equipo
                    ? (
                      otcAbiertasEquipo.length
                        ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {otcAbiertasEquipo.map((otc) => (
                              <OtcInlineLink
                                key={otc.id}
                                title={otc.titulo || 'Abrir OTC'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openLinkedOtc(otc.id);
                                }}
                              >
                                {`${otc.numero_ot || `OTC-${otc.id}`} - ${otc.titulo || '-'}`}
                              </OtcInlineLink>
                            ))}
                          </div>
                        )
                        : 'No se encontraron OTC abiertas para el equipo seleccionado.'
                    )
                    : 'Selecciona un equipo para detectar y vincular OTC abiertas automaticamente.'}
                </AutoInfo>
              </Field>
              <Field style={{ gridColumn: '1 / -1' }}>
                <label>Observaciones</label>
                <textarea value={detencionForm.observaciones || ''} onChange={(e) => setDetencionForm((v) => ({ ...v, observaciones: e.target.value }))} />
              </Field>
            </ModalGrid>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              {editingDetencion?.estado === 'en_parada' && (
                <ActionButton $variant="success" onClick={() => handleCompletarDetencion(editingDetencion)}>
                  <i className="bi bi-check-circle" /> Completar
                </ActionButton>
              )}
              {editingDetencion?.estado === 'egresado' && (
                <ActionButton $variant="warning" onClick={() => handleReabrirDetencion(editingDetencion)}>
                  <i className="bi bi-arrow-counterclockwise" /> Reabrir
                </ActionButton>
              )}
              <OutlinedButton $variant="secondary" onClick={() => { setShowDetencionModal(false); resetDetencionForm(); }}>
                Cancelar
              </OutlinedButton>
              <OutlinedButton onClick={handleSaveDetencion} disabled={savingDetencion}>
                {savingDetencion ? 'Guardando...' : 'Guardar'}
              </OutlinedButton>
            </div>
          </Modal>
        )}

        {showStandByModal && (
          <Modal title={editingStandBy ? `Editar Stand by #${editingStandBy.id}` : 'Nuevo Stand by'} onClose={cerrarStandByModal} size="lg">
            <ModalGrid>
              <Field style={{ gridColumn: '1 / -1' }}>
                <label>{editingStandBy ? 'Equipo' : 'Equipos'}</label>
                {editingStandBy ? (
                  <SearchableSelectField
                    options={opcionesEquiposStandBy}
                    value={standByForm.equipos[0] || ''}
                    onChange={(id) => setStandByForm((v) => ({ ...v, equipos: id ? [String(id)] : [] }))}
                    placeholder="Buscar equipo por interno o descripcion..."
                    emptyMessage="Sin equipos"
                    getOptionValue={(o) => o.value}
                    getOptionLabel={(o) => o.label}
                  />
                ) : (
                  <MultiSelectDropdown
                    size="md"
                    options={opcionesEquiposStandBy}
                    value={standByForm.equipos}
                    onChange={(equipos) => setStandByForm((v) => ({ ...v, equipos }))}
                    placeholder="Buscar equipo por interno o descripcion..."
                    etiquetaTodos="Seleccionar equipos..."
                    emptyMessage="Sin equipos"
                  />
                )}
                {!editingStandBy && standByForm.equipos.length > 0 && (
                  // El selector solo dice "N seleccionadas": acá se ve cuáles son, y se puede quitar uno.
                  <ChipsRow style={{ marginTop: 6 }}>
                    {standByForm.equipos.map((id) => {
                      const eq = equipoMap[id] || {};
                      return (
                        <Chip key={id} title={eq.descripcion || ''}>
                          <strong>{eq.interno || `EQ-${id}`}</strong>
                          {eq.descripcion ? ` ${eq.descripcion}` : ''}
                          <ChipButtonX
                            type="button"
                            title="Quitar"
                            onClick={() => setStandByForm((v) => ({ ...v, equipos: v.equipos.filter((x) => x !== id) }))}
                          >×</ChipButtonX>
                        </Chip>
                      );
                    })}
                  </ChipsRow>
                )}
              </Field>
              <Field>
                <label>Desde</label>
                <input type="date" value={standByForm.fecha_inicio} onChange={(e) => setStandByForm((v) => ({ ...v, fecha_inicio: e.target.value }))} />
              </Field>
              <Field>
                <label>Hasta (salida)</label>
                <input type="date" max={today} value={standByForm.fecha_fin || ''} onChange={(e) => setStandByForm((v) => ({ ...v, fecha_fin: e.target.value }))} />
              </Field>
              <Field style={{ gridColumn: '1 / -1' }}>
                <label>Motivo</label>
                <input type="text" maxLength={255} placeholder="Ej.: sin cliente asignado" value={standByForm.motivo} onChange={(e) => setStandByForm((v) => ({ ...v, motivo: e.target.value }))} />
              </Field>
              <Field style={{ gridColumn: '1 / -1' }}>
                <label>Observaciones</label>
                <textarea value={standByForm.observaciones} onChange={(e) => setStandByForm((v) => ({ ...v, observaciones: e.target.value }))} />
              </Field>
              <Field style={{ gridColumn: '1 / -1' }}>
                <AutoInfo>
                  Mientras el equipo esté en stand by, su próximo mantenimiento se calcula desde hoy y no figura
                  vencido por fecha (sí por horas). No cambia el estado del equipo ni su disponibilidad.
                  Dejá "Hasta" vacío si sigue en stand by.
                </AutoInfo>
              </Field>
            </ModalGrid>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              {editingStandBy?.esta_activo && (
                <ActionButton $variant="success" onClick={() => handleSalirStandBy(editingStandBy)}>
                  <i className="bi bi-play-circle" /> Registrar salida
                </ActionButton>
              )}
              {editingStandBy && !editingStandBy.esta_activo && (
                <ActionButton $variant="warning" onClick={() => handleReabrirStandBy(editingStandBy)}>
                  <i className="bi bi-arrow-counterclockwise" /> Reabrir
                </ActionButton>
              )}
              <OutlinedButton $variant="secondary" onClick={cerrarStandByModal}>
                Cancelar
              </OutlinedButton>
              <OutlinedButton onClick={handleSaveStandBy} disabled={savingStandBy}>
                {savingStandBy ? 'Guardando...' : 'Guardar'}
              </OutlinedButton>
            </div>
          </Modal>
        )}

        {quickCreateConfig && (
          <ConfiguracionBase
            apiEndpoint={quickCreateConfig.apiEndpoint}
            titulo={quickCreateConfig.titulo}
            descripcion={quickCreateConfig.descripcion}
            icon={quickCreateConfig.icon}
            compact
            modalOnly
            autoOpenAdd
            modalZIndex={1150}
            onRequestClose={closeQuickCreateModal}
            onRecordSaved={async (record) => {
              await refreshDetencionCatalogos();
              if (quickCreateConfig.type === 'motivo' && record?.id != null) {
                setDetencionForm((prev) => ({ ...prev, motivo_detencion: String(record.id) }));
              }
              if (quickCreateConfig.type === 'estado' && record?.id != null) {
                setDetencionForm((prev) => ({ ...prev, estado_equipo: String(record.id) }));
              }
              closeQuickCreateModal();
            }}
          />
        )}

        {showFiltersModal && (
          <Modal title="Filtros OTC" onClose={() => setShowFiltersModal(false)} size="md">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
              <DynamicFilterBuilder
                title="Filtrar por columnas"
                rows={esTabStandBy ? standByRows : (esTabFS ? detencionesRows : rows)}
                columns={filterColumns}
                value={dynamicFilters}
                onChange={setDynamicFilters}
              />

              <ModalGrid>
              <Field>
                <label>Apertura desde</label>
                <input type="date" value={filters.fecha_desde} onChange={(e) => setFilters((v) => ({ ...v, fecha_desde: e.target.value }))} />
              </Field>
              <Field>
                <label>Apertura hasta</label>
                <input type="date" value={filters.fecha_hasta} onChange={(e) => setFilters((v) => ({ ...v, fecha_hasta: e.target.value }))} />
              </Field>
              </ModalGrid>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <OutlinedButton $variant="secondary" onClick={() => {
                setFilters((f) => ({ ...f, fecha_desde: '', fecha_hasta: '' }));
                setDynamicFilters({ columnFilters: {}, touchedFilters: {}, expandedFilterGroups: {} });
              }}>
                Limpiar filtros
              </OutlinedButton>
              <OutlinedButton onClick={() => setShowFiltersModal(false)}>Cerrar</OutlinedButton>
            </div>
          </Modal>
        )}
      </Container>
    </PageShell>
  );
};

export default ListaReparaciones;
